import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TeamUser, UserRole, TeamUserFormData } from '../types/team.types';
import Button from '@/components/ui/button/Button';
import { useToast } from '@/components/ui/toast/Toast';

interface EditUserFormProps {
  user: TeamUser;
  onSave: (data: TeamUserFormData) => Promise<void>;
  onCancel: () => void;
}

export const EditUserForm: React.FC<EditUserFormProps> = ({
  user,
  onSave,
  onCancel
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<TeamUserFormData>({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    email: user.email || '',
    role: user.role
  });

  const handleInputChange = (field: keyof TeamUserFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSave(formData);
      toast.success(t('team.edit_user.save_success'));
    } catch (error) {
      toast.error(t('team.edit_user.save_error' + `<br/><br/>${error}`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold text-black dark:text-white mb-6">
        {t('team.edit_user.title')}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('team.edit_user.name')} (First)
            </label>
            <input
              type="text"
              value={formData.first_name}
              onChange={handleInputChange('first_name')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              placeholder="First name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('team.edit_user.name')} (Last)
            </label>
            <input
              type="text"
              value={formData.last_name}
              onChange={handleInputChange('last_name')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
              placeholder="Last name"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('team.edit_user.email')}
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            placeholder="user@example.com"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('team.edit_user.role')}
          </label>
          <select
            value={formData.role}
            onChange={handleInputChange('role')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
          >
            <option value="admin">{t('team.role.admin')}</option>
            <option value="seo">{t('team.role.seo')}</option>
          </select>
        </div>

        <div className="flex gap-3 pt-4">
          <Button
            htmlType="submit"
            variant="primary"
            disabled={loading}
            className="flex-1"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('team.edit_user.save')}
              </div>
            ) : (
              t('team.edit_user.save')
            )}
          </Button>

          <Button
            htmlType="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="flex-1"
          >
            {t('team.edit_user.cancel')}
          </Button>
        </div>
      </form>
    </div>
  );
};
